/* eslint-disable react/destructuring-assignment */
import React, { Component } from '@ali/odin/react';
import Markdown from './../Component/markdown';
import styles from '../index.less';

const markdown = 
`
## 互动内容技术规范

### 编写目的

本文档通过对优酷互动内容协议的详细解读，方便内容开发者快速加入到互动内容的基础设施研发中。我们将伴随平台发布，逐步开放自定义互动组件等模块的开发接入权限，敬请期待。

### 面向人群

具备一定开发能力的内容从业者。

### 涉及技术

[Weex](https://weex.apache.org/)，使开发者能基于通用跨平台的 Web 开发语言和开发经验，快速构建互动组件应用。

### 技术术语

| **概念**     | **英文**                     | **缩写** | **描述**                                                     |
| ------------ | ---------------------------- | -------- | ------------------------------------------------------------ |
| 互动内容平台 | Interactive Content Platform | ICP      | 服务端概念，用户用它来编辑一部互动内容。                       |
| 媒资平台     | Media Platform               | MP       | 存储视频、图片、音频信息；管理互动内容相关信息。               |
| 互动脚本     | Interactive Script           | IS       | 描述互动内容的核心文本，客户端基于此渲染互动内容。             |
| 互动引擎     | Interactive Engine           | IE       | 客户端概念，解析互动脚本，驱动整个互动内容的播放。             |
| 互动内容     | Interactive Content          | IC       | 为传统内容（比如视频剧集）添加上互动行为之后，形成的媒体。   |
| 互动视频     | Interactive Video            | IV       | 互动内容的一种，内容主要表现为视频形式；是一种统称。         |
| 剧           | Show                         |          | 在互动内容语境下讨论时，讲述一段相对完整而独立的故事，与传统剧的「节目(show)」相对应。 |
| 章           | Chapter                      |          | 在互动内容语境下讨论时，为互动内容的章，与传统剧的「选集(video)」相对应。 |
| 节点         | Node                         |          | 在互动内容语境下讨论时，为互动内容的粒子节点的容器，内容可能包含一段视频，或者一种交互形式等。 |
| 视频节点     | Video Node                   | VN       | 节点的一种，节点内容为视频。                                   |
| 互动节点     | Interactive Node             | IN       | 节点的一种，节点内容为互动页面。                               |
| 条件节点     | Condition Node               | CN       | 节点的一种，节点内容为判断条件。                               |
| 视频         | Video                        |          | 视频实体，表现为vid或者视频地址。                              |
| 事件列表     | Event List                   | EL       | 视频节点的属性，其中可能包含基于播放时间的事件列表。           |
| 事件         | Event                        |          | 视频节点上基于时间的事件，一般不会直接导致剧情分支。           |
| 用户资产     | User Assets                  | UA       | 与用户相关的信息、虚拟或实体资产集合。                         |
| 用户基本信息 | User Basic Profile           | UBP      | 用户姓名、年龄、联系方式等信息。                               |
| 用户存档     | User Saves                   | US       | 描述用户播放互动内容的进度信息或选择信息等。                   |



### 框架说明

互动内容主框架基于优酷播放器设计，为满足扩展性需求，引入互动引擎(Ice)和Weex容器。互动引擎(Ice)主要用于互动协议解析、互动内容播放、用户资产/行为上报。Weex则用来承接各种动态化互动组件。依托于Weex的动态特性，可以实现类似小游戏等更加复杂的互动形式。

![img](https://img.alicdn.com/tfs/TB146C5bUD1gK0jSZFGXXbd3FXa-1800-1230.png)

### 协议脚本设计

#### 概念定义

##### 互动内容流程节点(Node)

> 互动内容中的一个最小节点实例，其中包含一个组件实例(视频组件或互动组件或逻辑组件)，当组件为视频组件时，节点中还可包含一份数量为N(>=0)的事件节点合集；节点中还包含组件所需数据，以及下一节点信息。



##### 互动内容组件(Component)

>互动内容中的一个最小元件，代表了一个最小的完整功能单元，除功能描述外还额外包含入口&出口描述; 入口数量为N(N=1)，出口数量为O(O>=1)。

###### 1.视频组件(videoComponent) 

> N=1     O=1

用于视频片段展示的组件。

###### 2.互动组件(interactiveComponent)  

> N=1     O>=1

互动UI组件(Rax)，具体表现形式可以是A/B组件、QTE组件、探索组件、互动小游戏等，可由开发者按照『自定义组件开发流程』自行扩展。

> N=1     O=2

###### 3.逻辑组件(conditionComponent)         

无UI组件(Rax)，用于互动内容消费过程中对用户资产进行判断的需求。

###### 4.事件组件(eventComponent)  

>  N=1     O=1

UI组件(Rax)，最大的特点是对内容非阻断，会自行销毁的组件，并不会对剧情分支，可由开发者按照『自定义组件开发流程』自行扩展。

##### 互动内容资产(Assets)

> 在互动剧中资产本质为用户的信息与剧集物料，会在互动内容消费过程中进行变动。

#### 组件开发界限定义

互动组件划分基于两大原则：
- 阻塞与非阻塞 
- 及时响应分支&非及时响应分支

|      | **阻塞** | **非阻塞** |
| ------------ | ------------ | ------------ |
| **及时响应分支** | QTC,A/B,QTE | 条件组件 |
| **非及时相应分支** | 搜证/剧情探索 | TIP |

- 及时响应分支应该通过setExit处理
- 非及时响应分支应该通过资产控制

#### JSON定义

##### IRoot（协议结构）

| 字段                  | 类型                       | 描述         |
| --------------------- | -------------------------- | ------------ |
| entriesId             | Array<number>              | 入口节点ID   |
| nodeEntities          | Map<INode>                 | 剧情节点实例 |
| videoComponents       | Map<IVideoComponent>       | 视频组件列表 |
| interactiveComponents | Map<IInteractiveComponent> | 互动组件列表 |
| conditionComponents   | Map<IConditionComponent>   | 逻辑组件列表 |
| eventComponents       | Map<IEventComponent>       | 事件组件列表 |

##### INode（节点结构）

| 字段        | 类型          | 描述                                |
| ----------- | ------------- | ----------------------------------- |
| id          | Number        | ID                                  |
| type        | NodeType      | 节点类型                            |
| sign        | NodeSign      | 节点标识                            |
| name        | String        | 节点名称                            |
| componentId | Number        | 节点关联组件ID                      |
| next        | Array<number> | 出口节点ID列表                      |
| events      | Map           | 事件组件表(当type为video的时候存在) |
| x           | Number        | 搭建属性(x坐标)                     |
| y           | Number        | 搭建属性(y坐标)                     |
| data        | Any           | 搭建属性(x坐标)                     |

> type NodeType = 'video' | 'interactive' | 'condition' | 'event';
>
> type NodeSign = 'start' | 'normal' | 'successend'| 'failend';



整个协议JSON结构均由优酷YICP平台搭建生成，开发者并不需要过多关注协议结构，只需要关注互动组件的开发接入。


### 组件配置流程

组件配置流程说明中，我们以剧情分支组件和数值组件的配置过程为例，进行说明，具体如下：

#### 剧情分支组件配置

1.在内容制作页面，添加互动节点，选择节点组件；

![img](https://img.alicdn.com/tfs/TB12LhXb7L0gK0jSZFAXXcA9pXa-1800-1102.png)

2.点击【配置数据】进入组件配置页面，配置相关参数：

- 文字配置
  - 文字颜色：\`#DDD\`
  - 文字大小：\`10\`
  - 文字位置：\`居上\` | \`居左\` | \`居下\` | \`居右\`
  - 文字边距：\`100 50 20 150\` ;
- 背景配置
  - 背景(颜色或图片)：\`#333\` | \`https://img.alicdn.com/tfs/TB1WeJ9Xrj1gK0jSZFuXXcrHpXa-195-195.png\`
- 按钮配置
  - 按钮位置：\`居上\` | \`居左\` | \`居下\` | \`居右\`
  - 按钮边距：\`100 50 20 150\` ;
  - 边框宽度：\`1\`
  - 边框颜色：\`#DDD\`
  - 背景内外边距：\`5\`

![img](https://img.alicdn.com/tfs/TB16z4XbYr1gK0jSZR0XXbP8XXa-1800-1102.png)

#### 数值组件配置

1.进入内容制作页面，添加视频节点，点击开始配置；

![img](https://img.alicdn.com/tfs/TB1zm4ab4D1gK0jSZFKXXcJrVXa-1800-1102.png)

2.可视化组件配置界面，修改配置参数预览结果；

![img](https://img.alicdn.com/tfs/TB1dbZ.bUT1gK0jSZFhXXaAtVXa-1800-1102.png)

### 自定义组件开发流程

#### 名词解释

Hub：命令行工具，用于快速构建开发环境；  
[Rax][2]：阿里巴巴集团基于Weex容器，所推出的快速构建多端应用方案；

#### 开发环境

- 安装：node、npm
- 安装： 脚手架工具

\`\`\`bash
> npm install @ali/hub -g (对外版本待开放)
\`\`\`

- 安装Hub插件: \`@ali/hub-kit-ivc\`

\`\`\`bash
> hub plugin install @ali/hub-kit-ivc
\`\`\`

#### 开发流程

##### 项目初始化

\`\`\`
> hub init
\`\`\`

- 选择类型：ivc
- 生成组件：ivc-xxx

##### 目录解析

\`\`\`bash
ivc-ab-version
├── description：组件描述(名字、类型、出口等)
├── mock：组件配置数据（样式、内容等）
├── schema: 对应mock数据生成
├── src: 组件源代码
├── index.test.js: 本地测试入口
├── index.js: 线上入口
└── schema: 对应mock数据生成
\`\`\`

##### 本地调试

\`\`\`bash
# 本地调试，支持h5、weex扫码预览
> hub server
\`\`\`

##### 打包发布

\`\`\`bash
# 组件打包发布CDN服务器
> hub publish
\`\`\`

#### 容器API

- Ice: 客户端封装ice模块，提供容器层接口。

| API             | 描述         | 参数列表            |
| --------------- | ------------ | ------------------- |
| ice.close       | 关闭组件     |                     |
| ice.setExit     | 选择出口分支 | {value:'1'}         |
| ice.getUserInfo | 获取用户信息 |                     |
| ice.getValues   | 获取资产值   | {keys:[]}, callback |
| ice.setValues   | 设置资产值   | {key: value}        |

#### Demo

\`\`\`
/** @jsx createElement */
import { createElement, Component, findDOMNode } from 'rax';
import View from 'rax-view';
import Text from 'rax-text';

const styles = {
    tip: {
        width: 100,
        height: 200
    }
}

class Module extends Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  componentDidMount() {
    // 指定时间后关闭weex容器
    setTimeout(() => {
      ice && ice.close && ice.close();
    }, 3000);
  }

  render() {
    // data是组件配置数据 ； exit 是出口信息
    const { data,exit } = this.props.data || {};
    const { desc = {}, text, wrap, tip, avatar } = data;
    return (
      <View
        style={styles.tip} 
      >
          <Text style={{
            height: tip.height
          }} >
            这是一个tip
          </Text>
        </View>
      </View>
    );
  }
}
export default Module;

\`\`\`

[1]: https://kufe.youku.com
[2]: https://alibaba.github.io/rax/guide
[3]: https://gw.alicdn.com/tfs/TB1isWkbrH1gK0jSZFwXXc7aXXa-302-748.png


`

class Dev extends Component<{
  onChange: (obj: any) => void;
}> {
  constructor(props) {
    super(props);
  }
  render() {
    return <section className={styles.doc}>
      <Markdown markdown={markdown} title={'优酷互动内容技术标准'} onChange={this.props.onChange}/>
    </section>
  }
}

export default Dev;
